import styles from './NetworkSelector.module.css';

import type { FC } from 'react';
import type { ChainKey } from 'src/blockchain';
import type { PurchaseChainOption } from 'src/utils/purchaseChains';

import React from 'react';
import classNames from 'classnames';
import { CheckCircle } from '@phosphor-icons/react';

import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';

import { networkSelectorMode } from './networkSelectorMode';

interface NetworkSelectorProps {
  options: PurchaseChainOption[];
  selected: ChainKey | null;
  onSelect: (chainKey: ChainKey) => void;
}

/**
 * The network a Buy Now purchase settles on (multichain spec §4.3).
 *
 * Follows the pattern of a wallet app's network picker: a list of networks, each with its
 * settlement token, one of them selected. With a single available network there is no choice to
 * make, so it renders as a **badge** instead — showing a one-item list would ask the user to
 * decide something already decided, and this is the shape staging is in until Base is enabled.
 *
 * Which networks reach here is decided by `resolvePurchaseChainOptions`, never by this component:
 * it renders what it is given.
 */
export const NetworkSelector: FC<NetworkSelectorProps> = ({ options, selected, onSelect }) => {
  const mode = networkSelectorMode(options);

  if (mode === 'none') {
    return null;
  }

  if (mode === 'badge') {
    const [only] = options;

    return (
      <Stack gap={1} width="100%">
        <Typography color="lightWhite" variant="small">
          Network
        </Typography>
        <Stack flexDirection="row" gap={2} alignItems="center" className={styles.badge}>
          <NetworkIcon option={only} compact />
          <Typography color="white" variant="small" fontWeight="600">
            {only.displayName}
          </Typography>
          <Typography color="lightWhite" variant="small">
            {only.token.symbol}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={2} width="100%" className={styles.list}>
      <Typography color="lightWhite" variant="small">
        Pay on
      </Typography>
      {options.map((option) => (
        <div
          key={option.chainKey}
          data-testid={`network-option-${option.chainKey}`}
          className={classNames(styles.option, { [styles.selected]: selected === option.chainKey })}
          onClick={() => onSelect(option.chainKey)}
        >
          <Stack flexDirection="row" gap={2} alignItems="center" justifyContent="space-between">
            <Stack flexDirection="row" gap={2} alignItems="center">
              <NetworkIcon option={option} />
              <Stack>
                <Typography color="white" fontWeight="600">
                  {option.displayName}
                </Typography>
                <Typography color="lightWhite" variant="small">
                  Pay in {option.token.symbol}
                </Typography>
              </Stack>
            </Stack>
            {selected === option.chainKey ? (
              <CheckCircle size={20} color="#CCFF00" weight="fill" />
            ) : null}
          </Stack>
        </div>
      ))}
    </Stack>
  );
};

/**
 * Networks are identified by their initial rather than a logo file: the chain list is api's to
 * grow (Ethereum is one deploy away), and a registry that needs a new asset committed for every
 * chain would make "enabling a chain is config, not code" untrue on the front end.
 */
const NetworkIcon: FC<{ option: PurchaseChainOption; compact?: boolean }> = ({
  option,
  compact,
}) => (
  <div className={classNames(styles.icon, { [styles.badgeIcon]: compact })}>
    <Typography color="white" variant="small" fontWeight="600">
      {option.displayName.charAt(0).toUpperCase()}
    </Typography>
  </div>
);
